// src/components/production/OrderCard.tsx
import React, { useState, useCallback, memo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../UI/Button';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Check, Package, Truck, AlertCircle, ChevronDown, ChevronUp, XCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { Order, OrderStatus, ItemStatus } from '../../types/types';

const STATUS_COLORS: Record<OrderStatus, { color: string; icon: React.FC; label: string; progress: number }> = {
  pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'pending', progress: 0 },
  approved: { color: 'bg-teal-100 text-teal-800', icon: Check, label: 'approved', progress: 25 },
  in_production: { color: 'bg-purple-100 text-purple-800', icon: Package, label: 'in_production', progress: 50 },
  completed: { color: 'bg-green-100 text-green-800', icon: Check, label: 'completed', progress: 75 },
  in_transit: { color: 'bg-blue-100 text-blue-800', icon: Truck, label: 'in_transit', progress: 90 },
  delivered: { color: 'bg-emerald-100 text-emerald-800', icon: Check, label: 'delivered', progress: 100 },
  cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'cancelled', progress: 0 },
};

const ITEM_STATUS_COLORS: Record<ItemStatus, { label: string; color: string; icon: React.FC }> = {
  pending: { label: 'pending', color: 'bg-gray-50 text-gray-600', icon: Clock },
  assigned: { label: 'assigned', color: 'bg-blue-50 text-blue-600', icon: Check },
  in_progress: { label: 'in_progress', color: 'bg-yellow-50 text-yellow-600', icon: Package },
  completed: { label: 'completed', color: 'bg-green-50 text-green-600', icon: Check },
  cancelled: { label: 'cancelled', color: 'bg-red-50 text-red-600', icon: AlertCircle },
  rejected: { label: 'rejected', color: 'bg-red-50 text-red-600', icon: AlertCircle },
};

const PRIORITY_COLORS: Record<Order['priority'], string> = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

interface OrderCardProps {
  order: Order;
  calculateAdjustedTotal: (order: Order) => string;
  calculateTotalQuantity: (order: Order) => number;
  translateUnit: (unit: string, isRtl: boolean) => string;
  updateOrderStatus: (orderId: string, newStatus: OrderStatus) => void;
  openAssignModal: (order: Order) => void;
  submitting: string | null;
  isRtl: boolean;
}

// دالة التحقق من الصلاحيات
const canManageOrder = (userRole: string, orderStatus: string, action: 'approve' | 'cancel' | 'assign' | 'ship') => {
  // المدير العام: كل الصلاحيات في كل المراحل
  if (userRole === 'admin') {
    return true;
  }
  
  // المحاسب: يمكنه الموافقة أو الإلغاء فقط للطلبات pending
  if (userRole === 'accountant') {
    if (orderStatus === 'pending' && (action === 'approve' || action === 'cancel')) {
      return true;
    }
    return false;
  }
  
  // الإنتاج: يمكنه الموافقة أو الإلغاء فقط للطلبات pending
  if (userRole === 'production') {
    if (orderStatus === 'pending' && (action === 'approve' || action === 'cancel')) {
      return true;
    }
    if (orderStatus === 'approved' && action === 'assign') {
      return true;
    }
    if (orderStatus === 'completed' && action === 'ship') {
      return true;
    }
    return false;
  }
  
  return false;
};

const OrderCard: React.FC<OrderCardProps> = memo(
  ({ order, calculateAdjustedTotal, calculateTotalQuantity, translateUnit, updateOrderStatus, openAssignModal, submitting, isRtl }) => {
    const { user } = useAuth();
    const [isItemsExpanded, setIsItemsExpanded] = useState(false);
    const statusInfo = STATUS_COLORS[order.status] || STATUS_COLORS.pending;
    const StatusIcon = statusInfo.icon;
    const unassignedItems = order.items.filter((item) => !item.assignedTo && !item.rejected);
    const displayedItems = isItemsExpanded ? order.items : order.items.slice(0, 3);
    const hasMoreItems = order.items.length > 3;

    const toggleItemsExpanded = useCallback(() => {
      setIsItemsExpanded((prev) => !prev);
    }, []);

    const statusTranslations = {
      pending: isRtl ? 'قيد الانتظار' : 'Pending',
      approved: isRtl ? 'تم الموافقة' : 'Approved',
      in_production: isRtl ? 'في الإنتاج' : 'In Production',
      completed: isRtl ? 'مكتمل' : 'Completed',
      in_transit: isRtl ? 'في النقل' : 'In Transit',
      delivered: isRtl ? 'تم التسليم' : 'Delivered',
      cancelled: isRtl ? 'ملغى' : 'Cancelled',
    };

    const itemStatusTranslations = {
      pending: isRtl ? 'قيد الانتظار' : 'Pending',
      assigned: isRtl ? 'معين' : 'Assigned',
      in_progress: isRtl ? 'قيد التقدم' : 'In Progress',
      completed: isRtl ? 'مكتمل' : 'Completed',
      cancelled: isRtl ? 'ملغى' : 'Cancelled',
      rejected: isRtl ? 'مرفوض' : 'Rejected',
    };

    // دالة لتحديد الحالة التالية للمدير العام
    const getNextStatusForAdmin = (currentStatus: OrderStatus): OrderStatus => {
      switch (currentStatus) {
        case 'pending': return 'approved';
        case 'approved': return 'in_production';
        case 'in_production': return 'completed';
        case 'completed': return 'in_transit';
        case 'in_transit': return 'delivered';
        default: return currentStatus;
      }
    };

    // دالة لتحديد النص المناسب للمدير العام
    const getAdminButtonText = (currentStatus: OrderStatus, isRtl: boolean): string => {
      const texts = {
        pending: isRtl ? 'موافقة' : 'Approve',
        approved: isRtl ? 'بدء الإنتاج' : 'Start Production',
        in_production: isRtl ? 'إكمال' : 'Complete',
        completed: isRtl ? 'شحن' : 'Ship',
        in_transit: isRtl ? 'تسليم' : 'Deliver',
        cancelled: isRtl ? 'إلغاء' : 'Cancel',
        delivered: isRtl ? 'تم التسليم' : 'Delivered',
      };
      return texts[currentStatus] || (isRtl ? 'تقدم' : 'Next');
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-4"
        role="region"
        aria-labelledby={`order-${order.id}`}
      >
        <div className="p-3 bg-white shadow-md rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-300">
          <div className="flex flex-col gap-3">
            <div className={`flex items-center justify-between ${isRtl ? 'flex-row' : ''}`}>
              <div className="flex items-center gap-1">
                <h3 id={`order-${order.id}`} className="text-base font-semibold text-[15px] text-gray-800 truncate max-w-[220px]">
                  {isRtl ? `طلب ${order.orderNumber || 'غير معروف'}` : `Order #${order.orderNumber || 'Unknown'}`}
                </h3>
              </div>
              <span
                className={`px-1.5 py-0.5 rounded-full  text-[11px] font-medium flex items-center gap-1 ${statusInfo.color} ${
                  isRtl ? 'flex-row-reverse' : ''
                }`}
              >
                <StatusIcon className="w-3 h-3" />
                {statusTranslations[order.status]}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-amber-600 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${statusInfo.progress}%` }}
              />
            </div>
            {unassignedItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-1.5 bg-yellow-50 border border-yellow-100 rounded-md flex items-center gap-1.5"
                role="alert"
              >
                <AlertCircle className="w-3 h-3 text-yellow-600" />
                <span className="text-xs text-yellow-600">
                  {isRtl ? `${unassignedItems.length} عناصر غير معينة` : `${unassignedItems.length} unassigned items`}
                </span>
              </motion.div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <p className="text-xs text-gray-500">{isRtl ? 'الكمية الإجمالية' : 'Total Quantity'}</p>
                <p className="text-xs font-medium text-gray-800">
                  {isRtl ? `${calculateTotalQuantity(order)} عنصر` : `${calculateTotalQuantity(order)} items`}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isRtl ? 'إجمالي المبلغ' : 'Total Amount'}</p>
                <p className="text-xs font-semibold text-teal-600">{calculateAdjustedTotal(order)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isRtl ? 'التاريخ' : 'Date'}</p>
                <p className="text-xs font-medium text-gray-800 truncate">{order.date}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{isRtl ? 'الفرع' : 'Branch'}</p>
                <p className="text-xs font-medium text-gray-800 truncate">{order.branch.displayName || 'غير معروف'}</p>
              </div>
            </div>
            <div>
              <div className="p-2 bg-gray-50 rounded-md">
                <h4 className="text-xs font-semibold text-gray-900">{isRtl ? 'المنتجات' : 'Products'}</h4>
                <motion.div
                  initial={{ height: 'auto' }}
                  animate={{ height: 'auto' }}
                  transition={{ duration: 0.3 }}
                  className="mt-2"
                >
                  <ul className="space-y-2">
                    {displayedItems.map((item) => {
                      const itemStatusInfo = ITEM_STATUS_COLORS[item.status] || ITEM_STATUS_COLORS.pending;
                      const ItemStatusIcon = itemStatusInfo.icon;
                      const rejectedText = item.rejected ? (isRtl ? ` (مرفوض: ${item.rejectReason})` : ` (Rejected: ${item.rejectReason})`) : '';

                      // --- الكميات ---
                      const received = item.receivedQuantity || 0;
                      const requested = item.quantity;
                      const isOver = received > requested;
                      const isUnder = received < requested;
                      const showArrow = received > 0 && !item.rejected;

                      return (
                        <li
                          key={item._id}
                          className={`p-2 rounded-md ${item.rejected ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}
                        >
                          {/* اسم المنتج */}
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-medium text-gray-900 truncate flex-1">
                              {isRtl
                                ? `${item.displayProductName}${rejectedText}`
                                : `${item.displayProductName}${rejectedText}`}
                            </p>
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-xs font-small flex items-center gap-1 ${itemStatusInfo.color} ${
                                isRtl ? 'flex-row-reverse' : ''
                              }`}
                            >
                              <ItemStatusIcon className="w-3 h-3" />
                              {itemStatusTranslations[item.status]}
                            </span>
                          </div>

                          {/* --- الكميات أسفل الحالة --- */}
                          <div className="mt-1 flex items-center justify-between text-xs">
                            {item.assignedTo && (
                              <span className="text-xs text-blue-600">
                                {isRtl ? `شيف ${item.assignedTo.displayName}` : `Chef ${item.assignedTo.displayName}`}
                              </span>
                            )}
                          
                            {item.rejected ? (
                              <span className="text-red-700 flex items-center gap-1">
                                <XCircle className="w-3 h-3" />
                                {requested} {item.displayUnit}
                              </span>
                            ) : (
                              <div className="flex items-center gap-1 text-gray-700">
                                <span>{requested}</span>
                                <span className="text-gray-500">{item.displayUnit}</span>
                                {showArrow && (isRtl ? <ArrowLeft className="w-3 h-3 text-gray-500" /> : <ArrowRight className="w-3 h-3 text-gray-500" />)}
                                {showArrow && (
                                  <span className={isOver ? 'text-green-600 font-medium' : isUnder ? 'text-amber-600 font-medium' : 'text-gray-700'}>
                                    {received}
                                    <span className="text-gray-500 mx-1">{item.displayUnit}</span>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {hasMoreItems && (
                    <div className="flex justify-center items-center mt-2">
                      <button
                        onClick={toggleItemsExpanded}
                        className="text-amber-600 hover:text-amber-700 text-xs font-medium mt-2 flex items-center gap-1"
                        aria-expanded={isItemsExpanded}
                        aria-controls={`items-${order.id}`}
                      >
                        {isItemsExpanded ? (
                          <>
                            {isRtl ? 'عرض أقل' : 'Show Less'}
                            <ChevronUp className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            {isRtl ? `+${order.items.length - 3} منتج` : `+${order.items.length - 3} items`}
                            <ChevronDown className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
            {order.notes && (
              <div className="mt-1 p-1.5 bg-amber-50 rounded-md">
                <p className="text-xs text-amber-800 truncate">
                  <strong>{isRtl ? 'ملاحظات:' : 'Notes:'}</strong> {order.notes}
                </p>
              </div>
            )}
            {order.returns?.length > 0 && (
              <div className="mt-1 p-1.5 bg-amber-50 rounded-md">
                <p className="text-xs font-medium text-amber-800">{isRtl ? 'الإرجاعات' : 'Returns'}</p>
                {order.returns.map((r, i) => (
                  <p key={i} className="text-xs text-amber-700 truncate">
                    {isRtl
                      ? `${r.items
                          .map((item) => `${item.quantity} ${item.displayUnit} ${item.reason}`)
                          .join(', ')} - الحالة: ${
                          isRtl ? { pending: 'قيد الانتظار', approved: 'تمت الموافقة', rejected: 'مرفوض', processed: 'تمت المعالجة' }[r.status] : r.status
                        }`
                      : `${r.items
                          .map((item) => `${item.quantity} ${item.displayUnit} ${item.reason}`)
                          .join(', ')} - Status: ${r.status}`}
                  </p>
                ))}
              </div>
            )}
            
            {/* =========== قسم الأزرار المعدل =========== */}
            <div className={`flex flex-wrap gap-1.5 ${isRtl ? 'justify-end' : 'justify-start'}`}>
              {/* زر العرض المشترك */}
              <Link to={`/orders/${order.id}`}>
                <Button
                  variant="primary"
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-2.5 py-1 text-xs"
                  aria-label={isRtl ? `عرض طلب رقم ${order.orderNumber}` : `View order #${order.orderNumber}`}
                >
                  {isRtl ? 'عرض' : 'View'}
                </Button>
              </Link>
              
              {/* ===== أزرار المدير العام ===== */}
              {user?.role === 'admin' && order.status !== 'delivered' && order.status !== 'cancelled' && (
                <>
                  {/* زر التقدم (الموافقة/بدء الإنتاج/إكمال/شحن/تسليم) */}
                  <Button
                    variant="success"
                    size="sm"
                    icon={Check}
                    onClick={() => {
                      const nextStatus = getNextStatusForAdmin(order.status);
                      updateOrderStatus(order.id, nextStatus);
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1 text-xs"
                    disabled={submitting === order.id}
                    aria-label={isRtl ? `تقدم حالة الطلب ${order.orderNumber}` : `Advance order #${order.orderNumber}`}
                  >
                    {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : getAdminButtonText(order.status, isRtl)}
                  </Button>
                  
                  {/* زر الإلغاء */}
                  <Button
                    variant="danger"
                    size="sm"
                    icon={AlertCircle}
                    onClick={() => updateOrderStatus(order.id, 'cancelled')}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1 text-xs"
                    disabled={submitting === order.id}
                    aria-label={isRtl ? `إلغاء طلب رقم ${order.orderNumber}` : `Cancel order #${order.orderNumber}`}
                  >
                    {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : (isRtl ? 'إلغاء' : 'Cancel')}
                  </Button>
                </>
              )}
              
              {/* زر إعادة الفتح للمدير العام (للطلبات الملغية) */}
              {user?.role === 'admin' && order.status === 'cancelled' && (
                <Button
                  variant="success"
                  size="sm"
                  icon={Check}
                  onClick={() => updateOrderStatus(order.id, 'pending')}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full px-3 py-1 text-xs"
                  disabled={submitting === order.id}
                  aria-label={isRtl ? `إعادة فتح طلب ${order.orderNumber}` : `Reopen order #${order.orderNumber}`}
                >
                  {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : (isRtl ? 'إعادة فتح' : 'Reopen')}
                </Button>
              )}
              
            
              
              {/* ===== أزرار الإنتاج ===== */}
              {user?.role === 'production' && order.status === 'pending' && (
                <>
                  <Button
                    variant="success"
                    size="sm"
                    icon={Check}
                    onClick={() => updateOrderStatus(order.id, 'approved')}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1 text-xs"
                    disabled={submitting === order.id}
                    aria-label={isRtl ? `الموافقة على طلب رقم ${order.orderNumber}` : `Approve order #${order.orderNumber}`}
                  >
                    {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : (isRtl ? 'موافقة' : 'Approve')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={AlertCircle}
                    onClick={() => updateOrderStatus(order.id, 'cancelled')}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-3 py-1 text-xs"
                    disabled={submitting === order.id}
                    aria-label={isRtl ? `إلغاء طلب رقم ${order.orderNumber}` : `Cancel order #${order.orderNumber}`}
                  >
                    {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : (isRtl ? 'إلغاء' : 'Cancel')}
                  </Button>
                </>
              )}
              
              {user?.role === 'production' && order.status === 'approved' && unassignedItems.length > 0 && (
                <button
                  onClick={() => openAssignModal(order)}
                  className="bg-purple-500 hover:bg-purple-600 rounded-lg text-white px-3 py-1 text-xs flex items-center gap-1"
                  disabled={submitting === order.id}
                  aria-label={isRtl ? `تعيين طلب رقم ${order.orderNumber}` : `Assign order #${order.orderNumber}`}
                >
                  <Package className="w-3 h-3" />
                  {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : (isRtl ? 'توزيع' : 'Assign')}
                </button>
              )}
              
              {user?.role === 'production' && order.status === 'completed' && (
                <button
                  onClick={() => updateOrderStatus(order.id, 'in_transit')}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-1 text-xs flex items-center gap-1"
                  disabled={submitting === order.id}
                  aria-label={isRtl ? `شحن طلب رقم ${order.orderNumber}` : `Ship order #${order.orderNumber}`}
                >
                  <Truck className="w-3 h-3" />
                  {submitting === order.id ? (isRtl ? 'جارٍ...' : 'Loading...') : (isRtl ? 'شحن' : 'Ship')}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
);

OrderCard.displayName = 'OrderCard';
export default OrderCard;